/**
 * Collaborative text fields (PLAN §6.1, D6: "Yjs only for collaborative text
 * fields").
 *
 * A text field's replicated state is a Yjs document, stored base64 in
 * `row.text.<field>`; its materialised string is `row.f.<field>`, so a reader
 * that does not speak Yjs (the engine, a list view, search) still sees plain
 * text. A patch carries a Yjs *update*; applying it merges character by
 * character with every concurrent edit (PLAN §6.2 "Two people edit the same
 * script"), which is why these fields bypass the LWW rule.
 *
 * **The document's one shared type is a `Y.Text` named `TEXT_ROOT`.** Every
 * client must edit `doc.getText(TEXT_ROOT)`; anything else in a document is
 * carried along but never materialised.
 *
 * **Determinism.** `new Y.Doc()` draws a random client id. The functions that
 * only merge (`applyTextUpdate`, `mergeTextStates`) never write through that
 * id, so their output depends only on their inputs. `inverseTextUpdate` does
 * write — the undo is a new edit — so its client id is a parameter, the
 * writer's own.
 */
import * as Y from 'yjs';
import { decodeBase64, encodeBase64 } from './base64.js';

export const TEXT_ROOT = 'text';

export type TextStateResult =
  | { readonly ok: true; readonly state: string; readonly text: string; readonly changed: boolean }
  | { readonly ok: false; readonly error: string };

function decodeAll(parts: readonly string[]): Uint8Array[] | string {
  const out: Uint8Array[] = [];
  for (const part of parts) {
    const bytes = decodeBase64(part);
    if (bytes === null) return 'not base64';
    out.push(bytes);
  }
  return out;
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/** A fresh document holding `updates`, or the Yjs error that refused one. */
function docFrom(updates: readonly Uint8Array[]): Y.Doc | string {
  const doc = new Y.Doc();
  try {
    for (const update of updates) Y.applyUpdate(doc, update);
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
  return doc;
}

/**
 * Merge `updates` into `base` (null = a field never written), returning the new
 * state, its text, and whether anything changed. Never throws: a malformed
 * update is `ok: false`, and the caller nacks the patch.
 */
function mergeInto(base: string | null, updates: readonly string[]): TextStateResult {
  const decoded = decodeAll(base === null ? updates : [base, ...updates]);
  if (typeof decoded === 'string') return { ok: false, error: decoded };
  const before = base === null ? null : docFrom(decoded.slice(0, 1));
  const after = docFrom(decoded);
  if (typeof before === 'string') return { ok: false, error: `stored state: ${before}` };
  if (typeof after === 'string') return { ok: false, error: after };
  const beforeState = before === null ? null : Y.encodeStateAsUpdate(before);
  const afterState = Y.encodeStateAsUpdate(after);
  return {
    ok: true,
    state: encodeBase64(afterState),
    text: after.getText(TEXT_ROOT).toString(),
    changed: beforeState === null || !equalBytes(beforeState, afterState),
  };
}

/** Apply one Yjs update (base64) to a stored state (base64, or null for a field never written). */
export function applyTextUpdate(state: string | null, update: string): TextStateResult {
  return mergeInto(state, [update]);
}

/**
 * Merge two replicas' states of one text field — the text counterpart of
 * `mergeLwwRows` for whole-row catch-up. `changed` is relative to `local`.
 */
export function mergeTextStates(local: string, remote: string): TextStateResult {
  return mergeInto(local, [remote]);
}

/** The text a stored state materialises to, or null when the state is corrupt. */
export function materializeText(state: string): string | null {
  const result = mergeInto(null, [state]);
  return result.ok ? result.text : null;
}

/**
 * The Yjs update that undoes `update` on top of `stateBefore` (PLAN §6.2
 * "Undo … an undo is just a new patch"): Yjs's own `UndoManager` over the
 * one transaction, so the inverse removes exactly the characters `update`
 * inserted and restores exactly the ones it deleted — concurrent edits by
 * others survive, unlike "set the text back to what it was".
 *
 * `clientId` is the undoing writer's Yjs client id (a restored character is a
 * new insert by that writer). Null when either input does not decode.
 */
export function inverseTextUpdate(stateBefore: string | null, update: string, clientId: number): string | null {
  const decoded = decodeAll(stateBefore === null ? [update] : [stateBefore, update]);
  if (typeof decoded === 'string') return null;
  const updateBytes = decoded.at(-1);
  if (updateBytes === undefined) return null;
  const doc = docFrom(decoded.slice(0, -1));
  if (typeof doc === 'string') return null;
  doc.clientID = clientId;
  const origin = { undoTarget: true };
  const undo = new Y.UndoManager(doc.getText(TEXT_ROOT), { trackedOrigins: new Set([origin]), captureTimeout: 0 });
  try {
    Y.applyUpdate(doc, updateBytes, origin);
  } catch {
    return null;
  }
  const vector = Y.encodeStateVector(doc);
  undo.undo();
  undo.destroy();
  return encodeBase64(Y.encodeStateAsUpdate(doc, vector));
}

/**
 * A Yjs update (base64) that turns the text in `state` into `next`, as one
 * client's edit on top of that state. Shared by the CLI (`acp patch --text`)
 * and the SPA's script panel, which binds a textarea through it. Only the differing middle is replaced
 * (common prefix and suffix are kept), so characters someone else typed
 * concurrently outside that span survive the merge (PLAN §6.2).
 */
export function textReplaceUpdate(state: string | null, next: string, clientId: number): string {
  const doc = new Y.Doc();
  doc.clientID = clientId;
  if (state !== null) {
    const bytes = decodeBase64(state);
    if (bytes === null) throw new Error('the stored text state is not base64');
    Y.applyUpdate(doc, bytes);
  }
  const text = doc.getText(TEXT_ROOT);
  const current = text.toString();
  const before = Y.encodeStateVector(doc);
  let prefix = 0;
  while (prefix < current.length && prefix < next.length && current[prefix] === next[prefix]) prefix += 1;
  // Never cut a surrogate pair: Yjs would store the orphaned half as U+FFFD.
  if (prefix > 0 && isHighSurrogate(current.charCodeAt(prefix - 1))) prefix -= 1;
  let suffix = 0;
  while (
    suffix < current.length - prefix &&
    suffix < next.length - prefix &&
    current[current.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  if (suffix > 0 && isLowSurrogate(current.charCodeAt(current.length - suffix))) suffix -= 1;
  doc.transact(() => {
    const removed = current.length - prefix - suffix;
    if (removed > 0) text.delete(prefix, removed);
    const inserted = next.slice(prefix, next.length - suffix);
    if (inserted !== '') text.insert(prefix, inserted);
  });
  return encodeBase64(Y.encodeStateAsUpdate(doc, before));
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}
